import { cenario, percentuaisVariaveis, rateioFixaUnit } from './cenario.js';
/**
 * Todo percentual do vetor de cálculo vive em [0, 100). Fora disso, o motor
 * recusa: ver `RecomendacaoPercentualInvalido` para o caso medido que motivou
 * esta guarda.
 *
 * Por que 100 exclusivo e não inclusive: um único percentual em 100% já come o
 * preço inteiro, e a soma passando de 100% cai em `meta-inviavel`, que é a
 * mensagem certa para esse caso. Aqui é domínio de campo, não soma.
 *
 * Por que `Number.isFinite` junto: `parseNum` devolve 0 para texto, mas um
 * chamador desatento poderia passar NaN, que atravessaria toda comparação de
 * ordem sem disparar nenhuma.
 */
export const LIMITE_PERCENTUAL = 100;
function foraDaFaixa(valor) {
    return !Number.isFinite(valor) || valor < 0 || valor >= LIMITE_PERCENTUAL;
}
/**
 * percentuaisForaDeFaixa(p): todos os campos percentuais fora de [0, 100).
 *
 * Devolve lista, e não booleano, porque a tela precisa dizer QUAL campo está
 * errado. `campo` sai com o nome que o usuário vê, não com a chave do banco.
 */
export function percentuaisForaDeFaixa(p) {
    const fora = [];
    const conferir = (chave, campo, valor) => {
        if (foraDaFaixa(valor))
            fora.push({ chave, campo, valor });
    };
    // Os rótulos abaixo são os das telas, conferidos um a um, não inventados:
    // comissão, tributo e adicional em `telas/despesas.tsx`; a meta em
    // `telas/empresa.tsx` e `telas/preco/entradas.tsx`. Trocar um rótulo de
    // tela sem trocar aqui faz a mensagem apontar para um campo que não existe.
    conferir('comissao', 'Comissão de vendas', p.comissao);
    conferir('tributo', 'Carga tributária média', p.tributo);
    conferir('outras', 'Percentual adicional', p.outras);
    conferir('lucroAlvo', 'Meta de lucro líquido', p.lucroAlvo);
    // A extra sai com o nome que o próprio usuário deu a ela.
    for (const e of p.despesasExtras)
        conferir('extra', e.nome, e.valor);
    return fora;
}
/* preco.ts — as duas inversões da meta de lucro líquido.
 *
 * A mesma igualdade (lucro = alvo × faturamento) tem duas incógnitas úteis, e
 * cada uma responde a uma pergunta do integrador:
 *
 *   `precoSugerido`  → dado o volume, qual preço fecha a meta?
 *   `volumeParaMeta` → dado o preço praticado, qual volume fecha a meta?
 */
/**
 * precoSugerido(p): o PISO de preço para a meta de lucro líquido.
 *
 * Derivação (resolvendo lucroPct(ps) = alvo):
 *
 *   lucro(ps) = ps − cdt − [pctTotal·ps + pctServ·(ps − kit)] − fixUnit
 *   alvo·ps   = ps·(1 − pctTotal − pctServ) + pctServ·kit − cdt − fixUnit
 *   ⇒ ps = (cdt + fixUnit − pctServ·kit) / (1 − pctTotal − pctServ − alvo)
 *
 * O `+ pctServ·kit` no numerador (que aparece negativo na fórmula) não é
 * detalhe: quanto MAIOR o kit, MENOR a base de serviço, logo menos comissão e
 * tributo de base serviço — o preço sugerido cai. Esquecer esse termo era
 * cobrar percentual sobre o kit que ninguém cobra.
 *
 * Garantia estrutural, verificada em teste:
 *   cenario(precoSugerido(p).valor, p).lucroPct === p.lucroAlvo / 100
 *
 * Os dois motivos de recusa dizem coisas diferentes ao usuário:
 *   'sem-volume'    → falta DADO (quantos projetos por ano). Preenchível.
 *   'meta-inviavel' → os percentuais mais a meta somam 100% ou mais. Nenhum
 *                     preço resolve, porque cada real a mais de preço traz
 *                     junto mais comissão, mais tributo e mais meta. O que
 *                     resolve é baixar percentual ou baixar a meta.
 */
export function precoSugerido(p) {
    // Domínio ANTES de tudo, inclusive antes do volume. Dois motivos: com um
    // percentual fora de [0, 100) todo número daqui para baixo é sem sentido,
    // inclusive a própria recusa por volume; e um valor ERRADO já gravado é mais
    // urgente e mais acionável que um valor que falta.
    const invalidos = percentuaisForaDeFaixa(p);
    if (invalidos.length > 0) {
        return { valido: false, motivo: 'percentual-invalido', valor: null, invalidos };
    }
    const pcts = percentuaisVariaveis(p);
    const denom = 1 - pcts.total - pcts.serv - p.lucroAlvo / 100;
    // Volume primeiro: sem ele não existe rateio de fixa, e o preço nem começa
    // a ser calculável. Testar a meta antes diria "meta inviável" a quem só
    // esqueceu de preencher a sazonalidade.
    if (!(p.V12 > 0))
        return { valido: false, motivo: 'sem-volume', valor: null };
    // 1e-9 e não 0: com denominador microscópico o preço explodiria para as
    // centenas de milhões e a tela mostraria um número absurdo com cara de
    // resultado. "Inviável" é a resposta honesta.
    if (denom <= 1e-9)
        return { valido: false, motivo: 'meta-inviavel', valor: null };
    const fixUnit = rateioFixaUnit(p) ?? 0; // V12 > 0 já garantido acima
    const valor = (p.cdt + fixUnit - pcts.serv * p.kit) / denom;
    // A equação acima assume a raiz na região com base de serviço (ps >= kit).
    // Com custos normais isso é automático (cdt inclui o kit), mas um override
    // de custo direto abaixo do kit puxa a raiz para baixo dele — e aí vale o
    // clamp srvIncid = max(0, srv) de `cenario`: sem base de serviço, não há
    // percentual de serviço na equação. Resolver o outro trecho preserva a
    // garantia estrutural cenario(ps).lucroPct === alvo em todo o domínio, em
    // vez de publicar um "piso da meta" que entrega prejuízo.
    if (valor < p.kit) {
        return { valido: true, motivo: 'ok', valor: (p.cdt + fixUnit) / (denom + pcts.serv) };
    }
    return { valido: true, motivo: 'ok', valor };
}
/**
 * precoLucroZero(p): o preço em que o projeto para de consumir caixa.
 *
 * É a MESMA inversão de `precoSugerido`, com a meta zerada. Escrita como
 * delegação e não como fórmula copiada de propósito: são a mesma equação, e
 * duas cópias divergiriam no dia em que alguém corrigisse uma delas.
 *
 * Vale a identidade, que é o que liga os dois números e o que o teste verifica:
 *
 *   lucroUnit(ticket) = (1 − pctTotal − pctServ) × (ticket − precoLucroZero)
 *
 * O fator (1 − pctTotal − pctServ) é menor que 1, então distância em PREÇO e
 * distância em LUCRO não são a mesma coisa: R$ 50 abaixo do piso não é R$ 50 de
 * prejuízo. Confundir os dois faria o veredito de prejuízo disparar cedo demais
 * ou tarde demais, dependendo do lado.
 */
export function precoLucroZero(p) {
    return precoSugerido({ ...p, lucroAlvo: 0 });
}
/**
 * volumeParaMeta(p): quantos projetos por ano a meta exige AO PREÇO ATUAL.
 *
 * Do lucro anual (mcUnit·V − despFixa·12) igualado à meta (alvo·ticket·V):
 *
 *   V = (despFixa × 12) / (mcUnit − alvo·ticket)
 *
 * É a premissa 3 lida ao contrário: como a fixa rateia POR VOLUME, cada venda
 * a mais dilui o mesmo bolo de despesa fixa, e existe um volume a partir do
 * qual a diluição basta. Só existe, porém, se cada venda deixar margem acima
 * da meta.
 *
 * `null`, nunca Infinity nem número absurdo, em dois casos:
 *
 *   - denominador não positivo (margem de contribuição menor ou igual à meta):
 *     cada venda a mais traz junto a mesma margem curta, então nenhum volume
 *     fecha a conta. O que resolve é preço ou custo, não volume.
 *   - sem despesa fixa a cobrir: aí não existe volume MÍNIMO, a meta fecha em
 *     qualquer volume. Devolver 0 responderia outra pergunta.
 *
 * Arredonda para cima porque projeto é unidade inteira: o volume exato
 * truncado entregaria um pouco menos que a meta.
 */
export function volumeParaMeta(p) {
    // Mesma guarda de domínio de `precoSugerido`: o laudo chama esta função
    // direto, e sem a guarda uma meta inválida já gravada (ex.: −20 pontos)
    // publicaria um "volume que entrega a meta" MENOR que o honesto na mesma
    // análise em que o veredito recusou por percentual-invalido.
    if (percentuaisForaDeFaixa(p).length > 0)
        return null;
    const { mcUnit } = cenario(p.ticket, p);
    /** O que cada venda deixa ACIMA da meta, em R$. É o que dilui a fixa. */
    const denom = mcUnit - (p.lucroAlvo / 100) * p.ticket;
    // O corte é relativo ao preço, não zero absoluto, pelo mesmo motivo do 1e-9
    // de `precoSugerido`: margem que empata com a meta na décima segunda casa
    // decimal devolvia 1,9 × 10^17 projetos por ano, um absurdo com cara de
    // resultado. Ticket zerado também cai aqui, e é a resposta certa.
    if (!(denom > p.ticket * 1e-9))
        return null;
    const volume = Math.ceil((p.despFixa * 12) / denom);
    return volume > 0 ? volume : null;
}
//# sourceMappingURL=preco.js.map